import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GameLogo } from "@/components/GameLogo";
import { StateLogo } from "@/components/StateLogo";
import { useStateBySlug, useGamesForState, useStateResults } from "@/hooks/use-query-hooks";
import { formatJackpot, formatDate } from "@/lib/format";
import { NumberRow } from "@/components/NumberPill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExternalLink, Clock, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InstantGamesSection } from "@/components/InstantGamesSection";
import { instantGamesListQO } from "@/lib/instant-games.functions";
import { getStateFacts, stateIntro, stateMetaDescription, stateTitle, stateFactRows, buildStateFaqs } from "@/lib/page-copy";
import { stateOgImage } from "@/lib/og-images";
import { RelatedSections } from "@/components/RelatedSections";
import { relatedGamesForState, relatedStates } from "@/lib/related-links";
import { hasClaimGuide } from "@/data/claim-guides";
import {
  articleSchema,
  breadcrumbSchema,
  faqPageSchema,
  itemListSchema,
  stateSchema,
} from "@/lib/jsonld";
import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

export const Route = createFileRoute("/states/$stateSlug/")({
  component: StatePage,
  // Resolve the instant-games list during SSR so a deep link such as
  // /states/texas?ig=Crossword renders the filtered category in the first
  // paint instead of flashing "All" while the list loads client-side.
  loader: ({ context, params }) =>
    context.queryClient
      .ensureQueryData(instantGamesListQO(params.stateSlug))
      .catch(() => undefined),
  head: ({ params }) => {
    const name = params.stateSlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const path = `/states/${params.stateSlug}`;
    const facts = getStateFacts(params.stateSlug);
    const title = facts
      ? stateTitle(facts)
      : `${name} Lottery Results & Winning Numbers`;
    const description = facts
      ? stateMetaDescription(facts)
      : `Latest ${name} lottery results and winning numbers, today's jackpots, drawing schedules, and past winning numbers for every ${name} game.`;
    const image = stateOgImage(params.stateSlug);
    const relatedGameLinks = relatedGamesForState(params.stateSlug);
    const relatedStateLinks = relatedStates(params.stateSlug, params.stateSlug);
    return buildSeo({
      title,
      description,
      path,
      image,
      jsonLd: [
        breadcrumbSchema([
          { name: "Home", item: "/" },
          { name: "States", item: "/states" },
          { name: `${name} Lottery`, item: path },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${name} Lottery Games, Results & Jackpots`,
          url: path,
          about: `Lottery games, jackpots, and winning numbers for ${name}.`,
        },
        stateSchema({
          name,
          abbreviation: facts?.abbreviation ?? name,
          path,
          description: `${name} lottery information: games sold, winning numbers, jackpots and prize tax withholding.`,
        }),
        articleSchema({
          headline: `${name} Lottery Results & Winning Numbers`,
          description,
          path,
          image,
          section: "Lottery results",
          keywords: [`${name} lottery`, `${name} lottery results`, `${name} winning numbers`, "lottery jackpots"],
        }),
        ...(facts ? [faqPageSchema(buildStateFaqs(facts))] : []),
        itemListSchema(`Related ${name} lottery games`, relatedGameLinks),
        itemListSchema("Related state lottery hubs", relatedStateLinks),
      ],
    });
  },
});

function StatePage() {
  const { stateSlug } = Route.useParams();
  const { ig } = Route.useSearch() as { ig?: string };
  const { data: state } = useStateBySlug(stateSlug);
  const { data: games } = useGamesForState(stateSlug);
  const {
    data: latestDraws,
    isLoading: latestDrawsLoading,
    isError: latestDrawsError,
    refetch: refetchLatestDraws,
    isFetching: latestDrawsFetching,
  } = useStateResults(state?.id, 8);

  const distinctGamesInLatest = latestDraws
    ? new Set(latestDraws.map((r: any) => r.game_id).filter(Boolean)).size
    : 0;
  const totalStateGames = games?.length ?? 0;

  useEffect(() => {
    if (!state?.id || !latestDraws) return;
    trackEvent("state_latest_draws_rendered", {
      action: "state_latest_draws_rendered",
      page_type: "state",
      state_id: state.id,
      state_slug: state.slug,
      distinct_games_shown: distinctGamesInLatest,
      total_state_games: totalStateGames,
      rows_shown: latestDraws.length,
      coverage_ratio:
        totalStateGames > 0
          ? Math.round((distinctGamesInLatest / totalStateGames) * 100) / 100
          : null,
    });
  }, [state?.id, state?.slug, latestDraws, distinctGamesInLatest, totalStateGames]);

  if (!state) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <h1 className="font-display text-2xl font-bold">State Not Found</h1>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title={`${state.name} Lottery Results & Winning Numbers — Today's Draws & Jackpots`}
        description={`Latest ${state.name} lottery results and winning numbers, today's jackpots, drawing schedules, and past winning numbers.`}
      />
      <div className="container py-6 md:py-10">
        <div className="flex items-center gap-4 mb-6">
          <StateLogo stateSlug={state.slug} abbreviation={state.abbreviation} size="lg" />
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold">
              {state.name} Lottery Results &amp; Winning Numbers
            </h1>
            {latestDraws && latestDraws.length > 0 && (
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" /> Last drawn {formatDate(latestDraws[0].draw_date)}
              </p>
            )}
          </div>
        </div>

        {state.has_lottery && (() => {
          const facts = getStateFacts(state.slug);
          if (!facts) return null;
          return (
            <div className="mb-6 max-w-3xl">
              <p className="text-sm text-muted-foreground">{stateIntro(facts, totalStateGames)}</p>
              <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                {stateFactRows(facts, totalStateGames).map((row) => (
                  <div key={row.label} className="rounded-lg border border-border bg-muted/40 px-3 py-2">
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{row.label}</dt>
                    <dd className="text-sm font-medium">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })()}

        {state.has_lottery && (
          <section className="mb-10" aria-labelledby="latest-draws-heading">
            <div className="flex items-center justify-between mb-3">
              <h2 id="latest-draws-heading" className="font-display text-xl font-bold">
                Latest {state.name} winning numbers
              </h2>
              <Link to="/results" className="text-sm text-primary hover:underline">
                All results →
              </Link>
            </div>
            {latestDraws && latestDraws.length > 0 && (
              <>
            <p className="mb-3 text-xs text-muted-foreground">
              Showing {distinctGamesInLatest} {distinctGamesInLatest === 1 ? "game" : "games"}
              {totalStateGames > 0 ? ` of ${totalStateGames} available in ${state.name}` : ""}. Each row is the most recent draw for that game.
            </p>
            <Card className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Game</TableHead>
                    <TableHead className="w-[140px]">Last drawn</TableHead>
                    <TableHead>Winning Numbers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {latestDraws.map((r: any) => {
                    const g = r.games;
                    if (!g) return null;
                    const urlGameSlug = g.is_multistate
                      ? g.slug
                      : g.slug.startsWith(`${state.slug}-`)
                        ? g.slug.slice(state.slug.length + 1)
                        : g.slug;
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">
                          {g.is_multistate ? (
                            <Link to="/games/$gameSlug" params={{ gameSlug: g.slug }} className="text-primary hover:underline">
                              {g.name}
                            </Link>
                          ) : (
                            <Link to="/$stateSlug/$gameSlug" params={{ stateSlug: state.slug, gameSlug: urlGameSlug }} className="text-primary hover:underline">
                              {g.name}
                            </Link>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(r.draw_date)}
                        </TableCell>
                        <TableCell>
                          <NumberRow numbers={r.winning_numbers || []} bonusNumbers={r.bonus_numbers || []} size="sm" nowrap align="left" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
              </>
            )}
            {latestDrawsError && (
              <Card className="p-6 text-center">
                <div className="flex flex-col items-center gap-3">
                  <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
                  <div>
                    <p className="font-medium">We couldn't load the latest {state.name} draws.</p>
                    <p className="text-sm text-muted-foreground">Check your connection and try again.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchLatestDraws()}
                    disabled={latestDrawsFetching}
                    aria-label={`Retry loading latest ${state.name} winning numbers`}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${latestDrawsFetching ? "animate-spin" : ""}`} />
                    {latestDrawsFetching ? "Retrying…" : "Retry"}
                  </Button>
                </div>
              </Card>
            )}
            {!latestDrawsError && !latestDrawsLoading && (!latestDraws || latestDraws.length === 0) && (
              <Card className="p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  No recent draws available for {state.name} yet. Check back after the next official drawing.
                </p>
              </Card>
            )}
          </section>
        )}

        {!state.has_lottery ? (
          <div className="my-8 rounded-lg border border-border bg-muted/50 p-6 text-center">
            <p className="text-muted-foreground">{state.name} does not currently operate a state lottery.</p>
          </div>
        ) : (
          <section className="mb-10">
            <h2 className="font-display text-xl font-bold mb-4">All {state.name} lottery games</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {games?.map((g: any) => {
                const jp = g.jackpot_snapshots?.[0];
                const urlGameSlug = g.is_multistate
                  ? g.slug
                  : g.slug.startsWith(`${state.slug}-`)
                    ? g.slug.slice(state.slug.length + 1)
                    : g.slug;
                const cardInner = (
                  <Card className="hover:shadow-md hover:border-primary/40 transition-all h-full cursor-pointer">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <GameLogo gameSlug={g.slug} gameName={g.name} logoUrl={g.logo_url} size="sm" />
                        <div className="flex items-center justify-between flex-1">
                          <CardTitle className="text-base">{g.name}</CardTitle>
                          {g.is_multistate && <Badge variant="secondary" className="text-xs">Multi-State</Badge>}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {jp?.jackpot_amount > 0 && (
                        <p className="jackpot-amount text-2xl text-primary mb-1">{formatJackpot(jp.jackpot_amount)}</p>
                      )}
                      <p className="text-xs text-muted-foreground">{g.draw_schedule}</p>
                    </CardContent>
                  </Card>
                );
                return g.is_multistate ? (
                  <Link
                    key={g.id}
                    to="/games/$gameSlug"
                    params={{ gameSlug: g.slug }}
                    className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
                    aria-label={`View ${g.name}`}
                  >
                    {cardInner}
                  </Link>
                ) : (
                  <Link
                    key={g.id}
                    to="/$stateSlug/$gameSlug"
                    params={{ stateSlug: state.slug, gameSlug: urlGameSlug }}
                    className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
                    aria-label={`View ${g.name} in ${state.name}`}
                  >
                    {cardInner}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {state.has_lottery && (
          <InstantGamesSection stateSlug={state.slug} activeCategory={ig} />
        )}

        {state.official_lottery_url && (
          <div className="flex items-center gap-2 text-sm">
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Official: </span>
            <a
              href={state.official_lottery_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {state.name} Lottery Website
            </a>
          </div>
        )}

        {(() => {
          const facts = getStateFacts(state.slug);
          if (!facts) return null;
          const faqs = buildStateFaqs(facts, totalStateGames);
          return (
            <section className="mt-10" aria-labelledby="state-faq-heading">
              <h2 id="state-faq-heading" className="font-display text-xl font-bold mb-3">
                {state.name} lottery FAQs
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                {faqs.map((f) => (
                  <div key={f.q} className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                    <h3 className="text-sm font-semibold">{f.q}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          );
        })()}

        {hasClaimGuide(state.slug) && (
          <section className="mt-10 rounded-lg border border-border bg-muted/30 px-4 py-4">
            <h2 className="font-display text-lg font-bold">Won a prize in {state.name}?</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Where to claim by prize amount, the 180-day deadline, required documents, tax withholding and
              anonymity rules — sourced from the official {state.name} lottery.
            </p>
            <p className="mt-2 text-sm">
              <Link
                to="/states/$stateSlug/claim-prize"
                params={{ stateSlug: state.slug }}
                className="text-primary hover:underline"
              >
                How to claim a {state.name} lottery prize →
              </Link>
            </p>
          </section>
        )}

        <RelatedSections
          stateSlug={state.slug}
          games={relatedGamesForState(state.slug)}
          states={relatedStates(state.slug, state.slug)}
          gamesHeading={`Popular lottery games related to ${state.name}`}
          statesHeading="Other state lottery results"
        />

        <p className="mt-8">
          <Link to="/states" className="text-sm text-primary hover:underline">← All states</Link>
        </p>
      </div>
    </Layout>
  );
}